import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  apiPhotoCreate,
  apiPhotoDelete,
  apiPhotoGenerateUploadLink,
} from '../../actions/photoActions';
import { useSelector } from 'react-redux';
import axios from 'axios';
import CyrillicToTranslit from 'cyrillic-to-translit-js';

interface PhotoFile {
  id: string;
  size: string;
  filename: string;
  cancel: any;
  percent: number;
  completed: boolean;
  num: number;
  preview?: string;
  error?: boolean;
}

interface PhotoFilesProps {
  onChangeFiles?: (files: PhotoFile[]) => void;
}

function PhotoFiles({ onChangeFiles }: PhotoFilesProps) {
  const token = useSelector((state: any) => state.user.token);
  const user_id = useSelector((state: any) => state.user.user.id);
  const inputFile = useRef<HTMLInputElement>(null);
  const onChangeFilesRef = useRef(onChangeFiles);

  const [files, setFiles] = useState<PhotoFile[]>([]);

  useEffect(() => {
    onChangeFilesRef.current = onChangeFiles;
  }, [onChangeFiles]);

  useEffect(() => {
    if (onChangeFilesRef.current) {
      onChangeFilesRef.current(files);
    }
  }, [files]);

  const completeUpload = useCallback(
    async (tempId: string, filename: string) => {
      try {
        const response = await apiPhotoCreate(token, filename, user_id);
        if (response && response.photo_id) {
          setFiles(prev =>
            prev.map(f => {
              if (f.id === tempId) {
                return {
                  ...f,
                  id: response.photo_id,
                  percent: 100,
                  completed: true,
                };
              }
              return f;
            })
          );
        }
      } catch (error) {
        console.error('Error completing upload:', error);
      }
    },
    [token, user_id]
  );

  const changePercent = useCallback((id: string, percent: number) => {
    setFiles(prev =>
      prev.map(file => {
        if (file.id === id) {
          return { ...file, percent };
        }
        return file;
      })
    );
  }, []);

  const processFileUpload = useCallback(
    async (file: File) => {
      try {
        // @ts-ignore
        const cyrillicToTranslit = new CyrillicToTranslit();
        let filename = cyrillicToTranslit.transform(file.name, '_');
        filename = filename.replace(/[^a-zA-Z0-9.]/g, '').replaceAll(' ', '_');
        let parts = filename.split('.');
        let extensions = '';
        if (parts.length > 1) {
          extensions = parts[parts.length - 1];
        }
        if (filename.trim().length === 4) {
          filename = `${Math.floor(Date.now() / 1000)}.${extensions}`;
        }

        const fileSize = (file.size * 0.000001).toFixed(2);

        // Создаем превью для изображения
        const preview = URL.createObjectURL(file);

        // Генерируем ссылку для загрузки
        const generateLinkResponse = await apiPhotoGenerateUploadLink(token, filename, user_id);
        const uploadLink = generateLinkResponse.upload_link;

        const source = axios.CancelToken.source();
        const tempId = `temp_${Date.now()}_${Math.random()}`;

        // Добавляем файл в список
        setFiles(prev => {
          const newFile: PhotoFile = {
            id: tempId,
            size: fileSize,
            filename,
            cancel: source,
            percent: 0,
            completed: false,
            num: prev.length === 0 ? 0 : Math.max(...prev.map(f => f.num), -1) + 1,
            preview,
          };
          return [...prev, newFile];
        });

        // Загружаем файл на S3
        const config = {
          onUploadProgress: (progressEvent: any) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            changePercent(tempId, percent);
          },
          cancelToken: source.token,
          headers: {
            'Content-Type': file.type,
          },
        };

        try {
          await axios.put(uploadLink, file, config);
          await completeUpload(tempId, filename);
        } catch (error: any) {
          if (!axios.isCancel(error)) {
            setFiles(prev =>
              prev.map(f => (f.id === tempId ? { ...f, error: true, percent: 0 } : f))
            );
          }
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    },
    [token, user_id, changePercent, completeUpload]
  );

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const fileArray = Array.from(event.target.files);
      // Запускаем загрузку всех файлов сразу
      fileArray.forEach(file => {
        processFileUpload(file);
      });
      // Сбрасываем input, чтобы можно было выбрать те же файлы снова
      event.target.value = '';
    }
  };

  const deleteFile = (id: string) => {
    const file = files.find(f => f.id === id);
    if (file) {
      // Отменяем загрузку, если она еще идет
      if (file.cancel && !file.completed) {
        file.cancel.cancel('Upload cancelled by user');
      }

      // Освобождаем URL превью
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }

      setFiles(prev => prev.filter(f => f.id !== id));

      if (file.completed) {
        apiPhotoDelete(token, id, user_id);
      }
    }
  };

  const getUploadMB = (size: string, percent: number) => {
    return ((parseFloat(size) * percent) / 100).toFixed(2);
  };

  return (
    <>
      <div className="photo-files">
        {files.length === 0 ? (
          <div className="photo-files-empty">
            <span className="photo-files-empty-text">
              To start the upload select photos from your PC
            </span>
            <button
              className="photo-files-empty-button"
              onClick={() => {
                inputFile.current?.click();
              }}
            >
              Select files
            </button>
          </div>
        ) : (
          <>
            <div className="photo-files-grid">
              {files
                .sort((a, b) => (a.num > b.num ? 1 : b.num > a.num ? -1 : 0))
                .map((file, index) => (
                  <PhotoFileItem
                    key={file.id}
                    file={file}
                    onDelete={() => deleteFile(file.id)}
                    index={index}
                    getUploadMB={getUploadMB}
                  />
                ))}
            </div>
            <span
              className="photo-files-upload-button"
              onClick={() => {
                inputFile.current?.click();
              }}
            >
              Add photos
            </span>
          </>
        )}
        <input
          accept={'image/*'}
          type="file"
          multiple
          ref={inputFile}
          style={{ display: 'none' }}
          onChange={onFileChange}
        />
      </div>
    </>
  );
}

interface PhotoFileItemProps {
  file: PhotoFile;
  onDelete: () => void;
  index: number;
  getUploadMB: (size: string, percent: number) => string;
}

function PhotoFileItem({ file, onDelete, index, getUploadMB }: PhotoFileItemProps) {
  if (!file.completed) {
    return (
      <div className="photo-file-upload">
        <div className="photo-file-upload-preview">
          {file.preview && <img src={file.preview} alt={file.filename} />}
          {file.error && (
            <div className="photo-file-upload-error">
              <span className="fa fa-exclamation-triangle" />
              Upload error
            </div>
          )}
        </div>
        <div className="photo-file-upload-top">
          <span className="photo-file-upload-title" title={file.filename}>
            {file.filename}
          </span>
          <span className="photo-file-upload-close" onClick={onDelete} />
        </div>
        <div className="photo-file-upload-line">
          <div className="photo-file-upload-line-active" style={{ width: file.percent + '%' }} />
        </div>
        <div className="photo-file-upload-bottom">
          <span className="photo-file-upload-size">
            {getUploadMB(file.size, file.percent)} MB of {file.size} MB
          </span>
          <span className="photo-file-upload-percent">
            {file.error ? 'Error' : `Uploading... ${file.percent}%`}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="photo-file">
      <div className="photo-file-image">
        {file.preview ? (
          <img src={file.preview} alt={file.filename} />
        ) : (
          <div className="photo-file-placeholder">
            <span className="fa fa-image" />
          </div>
        )}
      </div>
      <div className="photo-file-content">
        <span className="photo-file-name" title={file.filename}>
          {file.filename}
        </span>
      </div>
      <div className="photo-file-icons">
        <span
          className="photo-file-icon fa fa-trash"
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
        />
      </div>
    </div>
  );
}

export default PhotoFiles;
